import { createLogger } from 'logging';
import { JSX } from 'react';
import { Plan } from 'subscription-service-client';
import { twMerge } from 'tailwind-merge';

import { useCheckoutContext } from './CheckoutContext';

const logger = createLogger('PlanSelectionBox');

export interface PlanSelectionBoxProps {
    isSelected: boolean;
    title: string;
    plan: Plan;
    onClick: (planCode: string) => void;
}

const getPlanIntervalString = (plan: Plan) => {
    // Question: Will monthly plans be defined as 1 month or 30 days?
    //   Similarly, will yearly plans be defined as 1 year, 12 months, or 365 days?
    if (
        (plan.intervalUnit == 'days' && plan.intervalLength == 365) ||
        (plan.intervalUnit == 'months' && plan.intervalLength == 12)
    ) {
        return 'annually';
    }
    return 'monthly';
};

export default function PlanSelectionBox({ isSelected, title, plan, onClick }: PlanSelectionBoxProps): JSX.Element {
    const { monthlyPlan, currency, currencySymbol, setCurrency } = useCheckoutContext();

    const getPlanPrice = (plan: Plan, currency: string) => {
        const currencies = plan.currencies;
        const foundCurrency = currencies?.filter((planCurrency) => planCurrency.currency === currency)[0];
        if (foundCurrency && foundCurrency.unitAmount) {
            return foundCurrency;
        }
        const usdFallBack = currencies?.filter((planCurrency) => planCurrency.currency === 'USD')[0];
        if (usdFallBack && usdFallBack.unitAmount) {
            setCurrency('USD');
            return usdFallBack;
        }
        return {
            currency: 'USD',
            unitAmount: 0.0,
        };
    };

    const planCurrency = getPlanPrice(plan, currency);

    const calculateSavings = () => {
        if (!monthlyPlan || getPlanIntervalString(plan) !== 'annually') {
            return null;
        }
        const monthlyPrice = getPlanPrice(monthlyPlan, currency).unitAmount ?? 0;
        const annualPrice = getPlanPrice(plan, currency).unitAmount ?? 0;
        const savings = monthlyPrice * 12 - annualPrice;
        return savings > 0 ? savings.toFixed(2) : null;
    };

    const handleClick = () => {
        // plan.code shouldn't be null, but just in case
        if (!plan?.code) {
            logger.error({ plan, title }, 'Plan code is missing or invalid');
            throw new Error('Plan code is missing or invalid.');
        }
        onClick(plan.code);
    };

    const savingsAmount = calculateSavings();

    return (
        <div
            className={twMerge(
                'w-full max-h-28 flex flex-row justify-between items-center bg-white rounded-sm p-4 border-2 border-white hover:border-blue-200',
                isSelected && 'border-blue-500 hover:border-blue-500',
                !isSelected && 'cursor-pointer',
            )}
            onClick={handleClick}
        >
            <div className="flex flex-col justify-center">
                <h3 className="text-base font-bold">{title}</h3>
                <p className="text-xs">
                    {currencySymbol}
                    {planCurrency.unitAmount} Billed {getPlanIntervalString(plan)}
                </p>
                {getPlanIntervalString(plan) === 'annually' && savingsAmount && (
                    <p className="mt-0 text-xs font-bold text-green-600">
                        Save {currencySymbol}
                        {savingsAmount}/year
                    </p>
                )}
            </div>
            <div
                className={twMerge(
                    'flex items-center justify-center w-6 h-6 border-2 rounded-full',
                    isSelected ? 'border-blue-500 bg-blue-500' : 'border-gray-300 bg-white hover:border-blue-300',
                )}
            >
                {isSelected && <div className="w-2 h-2 bg-white rounded-full"></div>}
            </div>
        </div>
    );
}
